import React, { ReactNode, useEffect, useMemo, useState } from "react";

// UX
import CardDrugDispensingInterfaceUX from "./CardDrugDispensingInterfaceUX";

// Common
import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import SnackMessage from "react-lib/apps/common/SnackMessage";
import { dateToStringBE } from "react-lib/utils/dateUtils";

// Utils
import { Checkbox, Icon } from "semantic-ui-react";

// Interface
import { useIntl } from "react-intl";
import {
  CARD_NAME,
  PickedProps,
  RunSequence,
  SetProp,
  State,
  SaveType,
  FilterSearchType,
  WidthsType,
  GenerateTableHeader,
} from "./sequence/DrugDispensingInterface";

// Serializer
import DrugDispensingInterfaceSerializer from "issara-sdk/types/DrugDispensingInterfaceSerializer_apps_INF";

// Type
type CardDrugDispensingInterfaceProps = {
  onEvent: (e: any) => any;
  setProp: SetProp;
  // seq
  runSequence: RunSequence;
  DrugDispensingInterfaceSequence: State["DrugDispensingInterfaceSequence"];
  languageUX: "en" | "th";
} & PickedProps;

const CardDrugDispensingInterface = (props: CardDrugDispensingInterfaceProps) => {
  const intl = useIntl();

  // useState
  const [openSnackMessage, setOpenSnackMessage] = useState<boolean>(false);
  const [selectedDrugDispensing, setSelectedDrugDispensing] = useState<any[]>([]);

  // useEffect
  useEffect(() => {
    props.runSequence({ sequence: "DrugDispensingInterface", restart: true });
  }, []);

  useEffect(() => {
    if (props.errorMessage?.[CARD_NAME] || props.successMessage?.[CARD_NAME]) {
      setOpenSnackMessage(true);
    }
  }, [props.errorMessage, props.successMessage]);

  const columnTableCenter = (data: any, style?: any) => {
    return <div style={{ ...style, display: "flex", justifyContent: "center" }}>{data ?? ""}</div>;
  };

  const columnTableIconCheck = (data: boolean, style?: any) => {
    return (
      <div style={{ ...style, display: "flex", justifyContent: "center" }}>
        {data === true ? (
          <Icon name="check circle" color="green" size="big" />
        ) : (
          <Icon name="times circle" color="red" size="big" />
        )}
      </div>
    );
  };

  const handleCheckAll = (_e: any, v: any) => {
    if (v.checked) {
      let newData = (props.DrugDispensingInterfaceSequence?.drugDispensingInterfaceList || []).map(
        (item: DrugDispensingInterfaceSerializer) => item.id
      );

      setSelectedDrugDispensing(newData);
    } else {
      setSelectedDrugDispensing([]);
    }
  };

  const handleSearch = () => {
    props.runSequence({
      sequence: "DrugDispensingInterface",
      action: "SEARCH",
      cardKey: CARD_NAME,
      page: 0,
    });

    setSelectedDrugDispensing([]);
  };

  const handleSave = (type: SaveType) => {
    props.runSequence({
      sequence: "DrugDispensingInterface",
      action: "SAVE",
      selectedItems: selectedDrugDispensing,
      cardKey: CARD_NAME,
      type,
    });

    setSelectedDrugDispensing([]);
  };

  const handleClear = () => {
    props.runSequence({
      sequence: "DrugDispensingInterface",
      action: "CLEAR",
      cardKey: CARD_NAME,
    });

    setSelectedDrugDispensing([]);
  };

  const handleChangeData = (_event: any, data: any) => {
    const name = data.name as keyof FilterSearchType;
    props.setProp(`DrugDispensingInterfaceSequence.searchDrugDispensing.${name}`, data?.value);
  };

  const handleChangeDate = (date: string) => {
    props.setProp(`DrugDispensingInterfaceSequence.searchDrugDispensing.createdDate`, date);
  };

  const updatePagination = (key: "page" | "pageSize", value: number) => {
    props.setProp(`DrugDispensingInterfaceSequence.paginatedItems.${key}`, value);

    props.runSequence({
      sequence: "DrugDispensingInterface",
      action: "SEARCH",
      cardKey: CARD_NAME,
      [key]: value,
    });

    setSelectedDrugDispensing([]);
    props.setProp(`DrugDispensingInterfaceSequence.selectedItems`, []);
  };

  // const handleDisabledTrigger = useMemo(() => {
  //   if (selectedDrugDispensing.length === 0) return true;

  //   return !selectedDrugDispensing.every((id) => {
  //     const row = props.DrugDispensingInterfaceSequence?.drugDispensingInterfaceList?.find(
  //       (item: DrugDispensingInterfaceSerializer) => item.id === id
  //     );

  //     return row?.valid === false;
  //   });
  // }, [selectedDrugDispensing, props.DrugDispensingInterfaceSequence?.drugDispensingInterfaceList]);

  // const handleDisabledSent = useMemo(() => {
  //   if (selectedDrugDispensing.length === 0) return true;

  //   return !selectedDrugDispensing.every((id) => {
  //     const row = props.DrugDispensingInterfaceSequence?.drugDispensingInterfaceList?.find(
  //       (item: DrugDispensingInterfaceSerializer) => item.id
  //     );

  //     return row?.valid === true && row?.sent === false;
  //   });
  // }, [selectedDrugDispensing, props.DrugDispensingInterfaceSequence?.drugDispensingInterfaceList]);

  const handleDisabledAddNEdit = useMemo(() => {
    if (selectedDrugDispensing.length === 0) return true;
  }, [selectedDrugDispensing]);

  // useMemo
  const drugDispensingInterfaceList = useMemo(() => {
    return (props.DrugDispensingInterfaceSequence?.drugDispensingInterfaceList || []).map(
      (item: DrugDispensingInterfaceSerializer) => {
        let createdName = (props.DrugDispensingInterfaceSequence?.userOptions || []).find(
          (data: DrugDispensingInterfaceSerializer) => data?.id === item.created_by
        );

        return {
          ...item,
          _checked: columnTableCenter(
            <Checkbox
              checked={selectedDrugDispensing.includes(item.id)}
              // disabled={}
              onChange={(e: any, v: any) => {
                if (v.checked) {
                  setSelectedDrugDispensing([...selectedDrugDispensing, item.id]);
                } else {
                  let newData = selectedDrugDispensing.filter((key: any) => key !== item.id);
                  setSelectedDrugDispensing(newData);
                }
              }}
            />,
            { padding: "10px" }
          ),
          ...tableHeaders.reduce(
            (data: Partial<Record<keyof DrugDispensingInterfaceSerializer, ReactNode>>, header) => {
              const key = header.accessor;

              if (key === "created_at") {
                data[key] = columnTableCenter(dateToStringBE(item[key]));
              } else if (key === "created_by") {
                data[key] = columnTableCenter(
                  (createdName?.full_name_code || "").replace(/\s*\(.*?\)/g, "")
                );
              } else if (["valid", "sent"].includes(key)) {
                data[key] = columnTableIconCheck(!!item[key]);
              } else if (["error_message", "reason_for_movement"].includes(key)) {
                data[key] = item[key];
              } else {
                data[key] = columnTableCenter(item[key]);
              }

              return data;
            },
            {}
          ),
        };
      }
    );
  }, [
    selectedDrugDispensing,
    props.DrugDispensingInterfaceSequence?.drugDispensingInterfaceList,
    props.DrugDispensingInterfaceSequence?.userOptions,
    tableHeaders,
  ]);

  return (
    <>
      {openSnackMessage && (
        <SnackMessage
          // func
          onEvent={props.onEvent}
          onClose={() => {
            props.setProp(`errorMessage.${CARD_NAME}`, null);
            props.setProp(`successMessage.${CARD_NAME}`, null);
          }}
          error={props.errorMessage?.[CARD_NAME]}
          success={props.successMessage?.[CARD_NAME]}
          languageUX={props.languageUX}
        />
      )}

      <CardDrugDispensingInterfaceUX
        onChangeDate={handleChangeDate}
        onChangeData={handleChangeData}
        onClearFilter={handleClear}
        searchDrugDispensing={props.DrugDispensingInterfaceSequence?.searchDrugDispensing}
        // table
        tableHeaders={[
          {
            Header: (
              <Checkbox
                checked={
                  selectedDrugDispensing.length ===
                    (props.DrugDispensingInterfaceSequence?.drugDispensingInterfaceList || [])
                      .length &&
                  (props.DrugDispensingInterfaceSequence?.drugDispensingInterfaceList || [])
                    .length > 0
                }
                onChange={handleCheckAll}
              />
            ),
            accessor: "_checked",
            width: 60,
          },
          ...tableHeaders,
        ]}
        drugDispensingInterfaceList={drugDispensingInterfaceList}
        paginatedItems={props.DrugDispensingInterfaceSequence?.paginatedItems}
        onPageSizeChange={(pageSize: number) => updatePagination("pageSize", pageSize)}
        onPageChange={(page: number) => updatePagination("page", page)}
        searchButton={
          <ButtonLoadCheck
            // func
            setProp={props.setProp}
            onClick={handleSearch}
            // data
            paramKey={`${CARD_NAME}_SEARCH`}
            buttonLoadCheck={props.buttonLoadCheck?.[`${CARD_NAME}_SEARCH`]}
            // config
            color="blue"
            title={intl.formatMessage({ id: "ค้นหา" })}
            name="SEARCH"
            size="medium"
          />
        }
        addButton={
          <ButtonLoadCheck
            // func
            setProp={props.setProp}
            onClick={() => handleSave("ADD")}
            disabled={handleDisabledAddNEdit}
            // data
            paramKey={`${CARD_NAME}_ADD`}
            buttonLoadCheck={props.buttonLoadCheck?.[`${CARD_NAME}_ADD`]}
            // config
            color="blue"
            title="Add Record"
            name="ADD"
            size="medium"
          />
        }
        editButton={
          <ButtonLoadCheck
            // func
            setProp={props.setProp}
            onClick={() => handleSave("EDIT")}
            disabled={handleDisabledAddNEdit}
            // data
            paramKey={`${CARD_NAME}_EDIT`}
            buttonLoadCheck={props.buttonLoadCheck?.[`${CARD_NAME}_EDIT`]}
            // config
            color="green"
            title="Edit Date"
            name="EDIT"
            size="medium"
          />
        }
      />
    </>
  );
};

CardDrugDispensingInterface.displayName = "CardDrugDispensingInterface";

export default React.memo(CardDrugDispensingInterface);

const columnWidths: WidthsType = {
  id: 70,
  valid: 70,
  error_message: 150,
  created_by: 120,
  created_at: 150,
  sent: 70,
  file_name: 160,
  version: 80,
  company_code: 120,
  reference_order_id: 120,
};

const tableHeaders = GenerateTableHeader(columnWidths);
